/**
 * Widget bar (#1822): one bar on the desktop instead of a separate Widget
 * Manager. It opens from the taskbar, lies over the windows and closes on a
 * click beside it or when the notification center opens.
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus, Plus } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { NOTIFICATION_CENTER_OPENED } from '@/components/system/NotificationBell'
import { useWidgetArea } from '@/contexts/WidgetAreaContext'
import { useWidgetSettings } from '@/contexts/WidgetSettingsContext'
import { useWindows } from '@/hooks/useWindows'
import {
  WIDGET_BAR_CATALOG,
  getActiveWidgetBarVariant,
  isWidgetBarSeeded,
  seedWidgetBar,
} from '@/data/widgets'
import type { AppId } from '@/data/apps'
import type { WidgetConfig, WidgetId } from '@/types/widget'
import type { WindowParams } from '@/types/window'
import { renderWidget } from '../WidgetRenderer'
import { PromptsPanel } from './PromptsPanel'
import { WidgetGallery } from './WidgetGallery'
import { isOutsideWidgetBar } from './outsideClick'
import { widgetCellClass } from './tile'

const PILL_CLASS =
  'rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'

function EditCell({
  config,
  onRemove,
  onSelect,
}: {
  config: WidgetConfig
  onRemove: () => void
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: config.id })
  const name = t(`widgetBar.names.${config.id}`)
  const variant = getActiveWidgetBarVariant(config)

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className={`relative h-[120px] touch-none ${config.size === 'small' ? '' : 'col-span-2'}`}
      data-widget-bar-item={config.id}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={onSelect}
        className="flex h-full w-full cursor-grab flex-col items-start gap-1 rounded-[20px] border border-[var(--window-border-color)] bg-[var(--window-background)] p-3.5 text-left active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]"
      >
        <span className="text-xs font-medium text-[var(--text-color)]">{name}</span>
        <span className="text-xs text-[var(--text-secondary)]">{t(`widgetBar.variants.${variant.id}`)}</span>
      </button>
      <button
        type="button"
        aria-label={t('widgetBar.remove', { name })}
        onClick={onRemove}
        className="absolute -left-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-[var(--window-border-color)] bg-[var(--window-background)] text-[var(--text-color)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]"
      >
        <Minus className="h-3 w-3" strokeWidth={3} aria-hidden />
      </button>
    </div>
  )
}

export function WidgetBar() {
  const { t } = useTranslation()
  const { isOpen, close, isEditMode, setEditMode, isPromptsPanelOpen, closePromptsPanel } = useWidgetArea()
  const { widgetConfigs, isLoading, reorderWidgets, disableWidget, updateWidgetSettings } = useWidgetSettings()
  const { openWindow } = useWindows()
  const [gallery, setGallery] = useState<{ selected?: WidgetId } | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    if (!isOpen) {
      setGallery(null)
      return
    }
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Element && isOutsideWidgetBar(event.target)) close()
    }
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener(NOTIFICATION_CENTER_OPENED, close)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener(NOTIFICATION_CENTER_OPENED, close)
    }
  }, [isOpen, close])

  useEffect(() => {
    if (!isLoading && !isWidgetBarSeeded(widgetConfigs)) reorderWidgets(seedWidgetBar(widgetConfigs))
  }, [isLoading, widgetConfigs, reorderWidgets])

  const barWidgets = useMemo(
    () =>
      widgetConfigs
        .filter((config) => config.enabled && WIDGET_BAR_CATALOG.includes(config.id))
        .sort((a, b) => a.position - b.position),
    [widgetConfigs],
  )

  const handleOpenApp = (appId?: AppId, params?: WindowParams) => {
    if (appId) openWindow(appId, undefined, undefined, params)
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const ids = barWidgets.map((config) => config.id)
    const moved = arrayMove(barWidgets, ids.indexOf(active.id as WidgetId), ids.indexOf(over.id as WidgetId))
    reorderWidgets([...moved, ...widgetConfigs.filter((config) => !moved.includes(config))])
  }

  if (!isOpen) return null

  return (
    <>
      <aside
        aria-label={t('widgetBar.title')}
        data-widget-area-panel
        className="fixed right-6 top-6 z-[13000] flex max-h-[calc(100vh-112px)] w-[368px] flex-col gap-3 overflow-y-auto p-4"
      >
        <div className="flex items-center justify-between py-1 pl-1.5 pr-1">
          <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-[var(--text-secondary)]">
            {t(isEditMode ? 'widgetBar.editing' : 'widgetBar.title')}
          </span>
          {isEditMode ? (
            <button
              type="button"
              onClick={() => {
                setEditMode(false)
                setGallery(null)
              }}
              className={`${PILL_CLASS} bg-[var(--theme-interactive-color)] font-bold text-[var(--theme-interactive-foreground)]`}
            >
              {t('widgetBar.done')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                closePromptsPanel()
                setEditMode(true)
              }}
              className={`${PILL_CLASS} border border-[var(--window-border-color)] text-[var(--text-color)]`}
            >
              {t('widgetBar.edit')}
            </button>
          )}
        </div>

        {isEditMode ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={barWidgets.map((config) => config.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-flow-row-dense grid-cols-2 gap-3">
                {barWidgets.map((config) => (
                  <EditCell
                    key={config.id}
                    config={config}
                    onRemove={() => disableWidget(config.id)}
                    onSelect={() => setGallery({ selected: config.id })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : barWidgets.length === 0 && !isLoading ? (
          <p className="px-1.5 py-6 text-center text-sm text-[var(--text-secondary)]">{t('widgetBar.empty')}</p>
        ) : (
          <div className="grid grid-flow-row-dense grid-cols-2 items-start gap-3">
            {barWidgets.map((config) => (
              <div key={config.id} className={widgetCellClass(config.id, config.size)} data-widget-bar-item={config.id}>
                {renderWidget(config.id, {
                  widgetId: config.id,
                  config,
                  onOpenApp: handleOpenApp,
                  onUpdateSettings: (settings) => updateWidgetSettings(config.id, settings),
                })}
              </div>
            ))}
          </div>
        )}

        {isEditMode && (
          <button
            type="button"
            onClick={() => setGallery({})}
            className="flex h-14 flex-shrink-0 items-center justify-center gap-2 rounded-[20px] border-[1.5px] border-dashed border-[var(--window-border-color)] text-sm font-medium text-[var(--text-color)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]"
          >
            <Plus className="h-[18px] w-[18px]" aria-hidden />
            {t('widgetBar.add')}
          </button>
        )}
      </aside>

      {isPromptsPanelOpen && <PromptsPanel />}

      {gallery && (
        <WidgetGallery available={WIDGET_BAR_CATALOG} initialSelected={gallery.selected} onClose={() => setGallery(null)} />
      )}
    </>
  )
}
